import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * EntityCombobox — searchable single-select for entity references
 * (customer, site, machine, engineer, machine type, etc.).
 *
 * Fixed enums (status, priority, frequency, slot type) should NOT use this —
 * they stay as regular `<Select>` for keyboard-friendly single-key selection.
 *
 * Props:
 *   items          [{ id, label?, name?, full_name?, ... }]  raw list; getLabel/getSecondary read from these.
 *   value          currently selected id (string) or ""/null for none.
 *   onChange(id)   called with the new id (or "" when the user picks the empty option).
 *   placeholder    trigger placeholder when no value.
 *   emptyMessage   text shown when the filtered list is empty.
 *   getLabel(item) label rendering (defaults to item.label ?? item.full_name ?? item.name).
 *   getSecondary   optional secondary line per row (e.g. serial, email).
 *   getSearch      optional string builder used by cmdk for matching (defaults to label + " " + secondary).
 *   allowClear     if true, show an "All / any / none" row that emits "".
 *   clearLabel     label for the clear/all row.
 *   disabled       disables the trigger.
 *   testid         base data-testid; trigger gets `${testid}` and items get `${testid}-item-${id}`.
 *   triggerClassName  optional className override for the trigger button.
 */
export function EntityCombobox({
  items,
  value,
  onChange,
  placeholder = "Select…",
  emptyMessage = "No matches.",
  getLabel,
  getSecondary,
  getSearch,
  allowClear = false,
  clearLabel = "— Any —",
  disabled = false,
  testid,
  triggerClassName,
}) {
  const [open, setOpen] = React.useState(false);
  const label = React.useCallback(
    (item) => (getLabel ? getLabel(item) : item.label ?? item.full_name ?? item.name ?? String(item.id)),
    [getLabel],
  );

  const selected = items.find((it) => it.id === value) || null;

  const search = React.useCallback(
    (item) => {
      if (getSearch) return getSearch(item);
      const l = label(item) || "";
      const s = getSecondary ? getSecondary(item) || "" : "";
      return `${l} ${s}`;
    },
    [getSearch, getSecondary, label],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // Match the visual weight/height of shadcn <SelectTrigger>: h-9, sharp corners, muted border.
            "w-full h-9 justify-between px-3 py-2 text-sm font-normal border-input bg-background hover:bg-background rounded-sm",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
          data-testid={testid}
        >
          <span className="truncate">{selected ? label(selected) : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
      >
        <Command>
          <CommandInput placeholder="Search…" autoFocus data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-item-clear` : undefined}
                  className="text-muted-foreground italic"
                >
                  <X className="h-3.5 w-3.5 mr-2 opacity-70" />
                  {clearLabel}
                </CommandItem>
              )}
              {items.map((item) => {
                const isSelected = item.id === value;
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.id} ${search(item)}`}
                    onSelect={() => {
                      onChange(item.id);
                      setOpen(false);
                    }}
                    data-testid={testid ? `${testid}-item-${item.id}` : undefined}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-3.5 w-3.5",
                        isSelected ? "opacity-100 text-primary" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">{label(item)}</span>
                      {getSecondary && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                          {getSecondary(item)}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * EntityMultiCombobox — searchable multi-select with checkbox items.
 *
 * Props (in addition to the single version):
 *   value            Array<string> of currently selected ids.
 *   onChange(ids)    called with the new array.
 *   summaryLabel     optional (values, items) => string for the trigger label.
 *                    Defaults to "N selected" / a single label / placeholder.
 */
export function EntityMultiCombobox({
  items,
  value,
  onChange,
  placeholder = "Select…",
  emptyMessage = "No matches.",
  getLabel,
  getSecondary,
  getSearch,
  summaryLabel,
  disabled = false,
  testid,
  triggerClassName,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedIds = React.useMemo(() => new Set(value || []), [value]);

  const label = React.useCallback(
    (item) => (getLabel ? getLabel(item) : item.label ?? item.full_name ?? item.name ?? String(item.id)),
    [getLabel],
  );

  const search = React.useCallback(
    (item) => {
      if (getSearch) return getSearch(item);
      const l = label(item) || "";
      const s = getSecondary ? getSecondary(item) || "" : "";
      return `${l} ${s}`;
    },
    [getSearch, getSecondary, label],
  );

  const summary = React.useMemo(() => {
    if (summaryLabel) return summaryLabel(value || [], items);
    if (!value || value.length === 0) return placeholder;
    if (value.length === 1) {
      const only = items.find((i) => i.id === value[0]);
      return only ? label(only) : "1 selected";
    }
    return `${value.length} selected`;
  }, [value, items, label, placeholder, summaryLabel]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full h-9 justify-between px-3 py-2 text-sm font-normal border-input bg-background hover:bg-background rounded-sm",
            (!value || value.length === 0) && "text-muted-foreground",
            triggerClassName,
          )}
          data-testid={testid}
        >
          <span className="truncate">{summary}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]"
      >
        <Command>
          <CommandInput placeholder="Search…" autoFocus data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => {
                const checked = selectedIds.has(item.id);
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.id} ${search(item)}`}
                    onSelect={() => toggle(item.id)}
                    data-testid={testid ? `${testid}-item-${item.id}` : undefined}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mr-2 h-4 w-4 border border-input rounded-sm flex items-center justify-center shrink-0",
                        checked ? "bg-primary border-primary" : "bg-background",
                      )}
                    >
                      {checked && <Check className="h-3 w-3 text-primary-foreground" />}
                    </span>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm truncate">{label(item)}</span>
                      {getSecondary && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                          {getSecondary(item)}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default EntityCombobox;
